import React, {
	useContext,
	createContext,
	useState,
	useCallback,
	useRef,
	type ComponentType,
} from 'react';
import noop from 'lodash/noop';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Any = any;

type FormProps = {
	[key: string]: Any;
};

type FormContextData = [
	{
		isFormDirty: boolean;
		showNavBlocker?: () => void;
		getNextCalendarActionCallback: () => void;
	},
	{
		setFormStatusToDirty: () => void;
		onRegisterBlock: (arg1: Any) => () => void;
		setNextCalendarActionCallback: (arg1: Any) => void;
	},
];

export const FormContext = createContext<FormContextData>([
	{
		isFormDirty: false,
		showNavBlocker: undefined,
		getNextCalendarActionCallback: noop,
	},
	{
		setFormStatusToDirty: noop,
		onRegisterBlock: () => noop,
		setNextCalendarActionCallback: noop,
	},
]);

/**
 *  withFormContextProvider
 *  AkForm doesn't change form status to dirty if one of the fields are removed
 *  this context have state & actions to set the form status to dirty
 */
export const withFormContextProvider =
	(FromComponent: ComponentType<Any>) => (props: FormProps) => {
		const [isFormDirty, setIsFormDirty] = useState(false);
		const [showNavBlocker, setNavBlockerCallback] = useState<unknown>();
		const nextCalendarActionCallback = useRef<() => void>(noop); // This would store the next calendar callback

		// @ts-expect-error - TS7006 - Parameter 'eventToBeRegistered' implicitly has an 'any' type.
		const onRegisterBlock = useCallback((eventToBeRegistered) => {
			setNavBlockerCallback(() => eventToBeRegistered);
			return noop;
		}, []);

		const setFormStatusToDirty = useCallback(() => {
			setIsFormDirty(true);
		}, []);

		const getNextCalendarActionCallback = useCallback(() => {
			nextCalendarActionCallback.current();
		}, []);

		// @ts-expect-error - TS7006 - Parameter 'callback' implicitly has an 'any' type.
		const setNextCalendarActionCallback = useCallback((callback) => {
			nextCalendarActionCallback.current = callback;
		}, []);

		const formStatusValue = [
			{
				isFormDirty,
				showNavBlocker,
				getNextCalendarActionCallback,
			},
			{ setFormStatusToDirty, onRegisterBlock, setNextCalendarActionCallback },
		];

		return (
			// @ts-expect-error - TS2322 - Type '({ isFormDirty: boolean; showNavBlocker: unknown; getNextCalendarActionCallback: () => void; setFormStatusToDirty?: undefined; onRegisterBlock?: undefined; setNextCalendarActionCallback?: undefined; } | { ...; })[]' is not assignable to type 'FormContextData'.
			<FormContext.Provider value={formStatusValue}>
				<FromComponent {...props} />
			</FormContext.Provider>
		);
	};

export const useFormContext = () => useContext(FormContext);
